import BaseModel from './BaseModel';

import * as AT from '../../components/container/attributes';
import Address from './Address';
import AddressBankAccount from './AddressBankAccount';
import Journal from './Journal';
import Period from './Period';
import ProjectPart from './ProjectPart';
import {required, helpers, requiredIf} from '@vuelidate/validators';
import {DataService} from '../../index';
import StaffList from './joinList/StaffList';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {
  HISTORY_STATUS_FILTER_OPTIONS,
  hasStatusThatDisablesEditing,
} from 'hit-online-web-ui/src/helpers/transactionStatusWorkflow';
import {useConfigurationStore} from '../../store/pinia/configuration';

const isValidTransactionDate = (date) => {
  if (!date) return false;
  const minDate = new Date('1970-01-01');
  const dateDate = new Date(date);
  return dateDate >= minDate;
};

const documentDateRequired = helpers.withParams(
  {type: 'documentDateRequired'},
  isValidTransactionDate,
);

const dueDateRequired = helpers.withParams(
  {type: 'dueDateRequired'},
  isValidTransactionDate,
);

/** Required when the matching bookkeeping.responsible-*.mandatory config is enabled for the journal type. */
const responsiblesRequiredIf = requiredIf(function (_value, siblings) {
  const journalType =
    siblings?.journal?.journalType?.id ?? siblings?.journal?.journal_type;
  const config = useConfigurationStore();
  if (journalType === 'PURCHASE') {
    return !!config.getConfiguration(
      'bookkeeping.responsible-purchase.mandatory',
      false,
    );
  }
  if (journalType === 'SALE') {
    return !!config.getConfiguration(
      'bookkeeping.responsible-sale.mandatory',
      false,
    );
  }
  return false;
});

export default class Transaction extends BaseModel {
  constructor({
    id,
    documentNumber,
    designation,
    address = undefined,
    projectPart = undefined,
    journal = undefined,
    period = undefined,
    documentDate = undefined,
    dueDate = undefined,
    discountPercentage = undefined,
    discountDays = undefined,
    discountEndDate = undefined,
    discountAmount = undefined,
    paymentReference = null,
    header = '',
    description = '',
    footer = '',
    transacted = false,
    peppolInvoiceInbound = false,
    peppolInvoiceOutbound = false,
    peppolDocumentType = null,
    manualEdit = false,
    tags = [],
    responsibles = [],
    documents = [],
    logs = [],
    oldDocumentNumber = null,
    warning = {},
    memo = '',
    bankAccount = undefined,
    status = null,
  }) {
    super(id, documentNumber, designation);

    this.documentNumber = documentNumber;
    this.address = address ? new Address(address) : null;
    this.projectPart = projectPart ? new ProjectPart(projectPart) : null;
    this.journal = journal ? new Journal(journal) : null;
    this.period = period ? new Period(period) : null;
    this.documentDate = documentDate;
    this.documentNumber = documentNumber;
    this.dueDate = dueDate;
    this.discountPercentage = discountPercentage ?? null;
    this.discountDays = discountDays ?? null;
    this.discountEndDate = discountEndDate ?? null;
    this.discountAmount = discountAmount ?? null;
    this.paymentReference = paymentReference;
    this.header = header;
    this.description = description;
    this.footer = footer;
    this.transacted = transacted;
    this.peppolInvoiceInbound = peppolInvoiceInbound;
    this.peppolInvoiceOutbound = peppolInvoiceOutbound;
    this.peppolDocumentType = peppolDocumentType;
    this.manualEdit = manualEdit;
    this.tags = tags;
    this.responsibles = responsibles ? new StaffList(responsibles) : [];
    this.documents = documents ? documents : [];
    this.logs = logs ? logs : [];
    this.oldDocumentNumber = oldDocumentNumber;
    this.warning = warning ? warning : {};
    this.memo = memo;
    this.status = status;
    this.bankAccount = bankAccount ? new AddressBankAccount(bankAccount) : null;
  }

  static get orderKey() {
    return 'document_no';
  }
  static get orderOrder() {
    return 'desc.nullslast';
  }

  get isPurchaseType() {
    return this.journal?.journalType?.id === 'PURCHASE';
  }

  /** Returns the list of the PDF ids to display in the viewer ordered by creation date DESC */
  get pdfIdToDisplayInViewer() {
    const pdfs = this.documents
      .filter((doc) => doc.mime_type === 'application/pdf')
      .sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date));
    return pdfs?.map((file) => file.id) ?? [];
  }

  static get config() {
    return {
      addable: true,
      duplicatable: false,
      importable: false,
      browsable: true,
      deletable: false,
    };
  }
  get config() {
    return Transaction.config;
  }

  static get defaultSort() {
    return {
      property: 'documentDate',
      order: 'desc',
    };
  }

  getDefaultSort() {
    return Transaction.defaultSort;
  }

  static get printTemplateType() {
    return 'transaction';
  }
  get printTemplateType() {
    if (this.isPurchaseType) return null;
    return Transaction.printTemplateType;
  }

  static get fkName() {
    return 'transaction_id';
  }
  static get apiRoute() {
    return 'transaction';
  }
  static get detailPageRoute() {
    return 'transactionDetail';
  }
  static get listPageRoute() {
    return 'transactionList';
  }
  get fkName() {
    return Transaction.fkName;
  }
  get apiRoute() {
    return Transaction.apiRoute;
  }
  get detailPageRoute() {
    return Transaction.detailPageRoute;
  }
  get listPageRoute() {
    return Transaction.listPageRoute;
  }

  static get joinAttrsKey() {
    return ['documentNumber', 'designation', 'address', 'projectPart'];
  }

  static get listDisplayAttrs() {
    return this.getAttributes(
      [
        'designation',
        'documentNumber',
        'journal',
        'documentDate',
        'dueDate',
        'discountEndDate',
        'discountAmount',
        'address',
        'projectPart',
        'transactionAmount',
        'transactionAmountGross',
        'tags',
        'period',
        'responsibles',
        'creationDate',
        'modificationDate',
        'logs',
      ],
      [
        'documents',
        'peppolInvoiceInbound',
        'peppolInvoiceOutbound',
        'peppolDocumentType',
        'warning',
      ],
    );
  }

  static get entityListDisplayAttrs() {
    return this.getAttributes([
      'designation',
      'documentNumber',
      'documentDate',
      'journal',
      'address',
      'transactionAmount',
      'tags',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'designation',
      'journal',
      'period',
      'documentNumber',
      'documentDate',
      'address',
      'projectPart',
      'tags',
    ]);
  }

  static get createInitColumns() {
    return {
      designation: '',
      document_date: new Date(),
    };
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes(['tags']),
    };
  }

  static get allAttributes() {
    return [
      this.DESIGNATION_ATTRIBUTE({}, {maxWidth: '2fr'}),
      new AT.IntegerAttribute({
        name: 'documentNumber',
        column: 'document_no',
        tableProperties: {
          header: 'hit-app.transaction.document-number',
          maxWidth: '0.75fr',
        },
      }),

      new AT.FkAddressAttribute({
        name: 'address',
        validations: {required},
        joinPath: ['address!fk_transaction_address_id_address'],
        tableProperties: {
          header: 'hit-base.common.address',
        },
      }),
      new AT.FkProjectPartAttribute({
        name: 'projectPart',
        joinPath: ['project_part!fk_transaction_project_part_id_project_part'],
        tableProperties: {
          header: 'hit-base.common.project-part',
        },
      }),
      new AT.FkJournalAttribute({
        name: 'journal',
        validations: {required},
        joinPath: ['journal!fk_transaction_journal_id_journal'],
        tableProperties: {
          header: 'hit-app.common.journal',
          maxWidth: '1fr',
        },
      }),
      new AT.FkPeriodAttribute({
        name: 'period',
        joinPath: ['period!fk_transaction_period_id_period'],
        filters: {closed: 'is.false'},
        tableProperties: {
          header: 'hit-app.common.period',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'documentDate',
        validations: {documentDateRequired},
        column: 'document_date',
        tableProperties: {
          header: 'hit-app.transaction.document-date',
          maxWidth: '1fr',
        },
      }),
      new AT.DateAttribute({
        name: 'dueDate',
        validations: {dueDateRequired},
        column: 'due_date',
        tableProperties: {
          header: 'hit-app.transaction.due-date',
        },
      }),
      new AT.DecimalAttribute({
        name: 'discountPercentage',
        column: 'discount_percentage',
        decimalPrecision: 2,
        decimalKeepNullValue: true,
        tableProperties: {
          header: 'hit-app.common.cash-discount-percentage',
          hideByDefault: true,
        },
      }),
      new AT.IntegerAttribute({
        name: 'discountDays',
        column: 'discount_days',
        tableProperties: {
          header: 'hit-app.common.cash-discount-days',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'discountEndDate',
        column: 'discount_end_date',
        tableProperties: {
          header: 'hit-app.common.cash-discount-end-date',
          hideByDefault: true,
        },
      }),
      new AT.NumberAttribute({
        name: 'discountAmount',
        column: 'discount_amount',
        tableProperties: {
          header: 'hit-app.common.cash-discount-amount',
          hideByDefault: true,
        },
      }),
      this.HEADER_RICH_TEXT_ATTRIBUTE(),
      this.DESCRIPTION_RICH_TEXT_ATTRIBUTE(),
      this.FOOTER_RICH_TEXT_ATTRIBUTE(),
      new AT.BooleanAttribute({
        name: 'transacted',
        tableProperties: {
          header: 'transacted',
        },
      }),
      new AT.BooleanAttribute({
        name: 'peppolInvoiceInbound',
        column: 'peppol_invoice_inbound',
        tableProperties: {
          header: 'hit-app.transaction.peppol-invoice-inbound',
          maxWidth: '0.75fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'peppolInvoiceOutbound',
        column: 'peppol_invoice_outbound',
        tableProperties: {
          header: 'hit-app.transaction.peppol-invoice-outbound',
          maxWidth: '0.75fr',
        },
      }),
      new AT.StringAttribute({
        name: 'peppolDocumentType',
        column: 'peppol_document_type',
        tableProperties: {
          hideByDefault: true,
        },
      }),
      new AT.NotFetchedAttribute({
        name: 'viaPeppol',
        tableProperties: {
          header: 'Peppol',
          maxWidth: '0.75fr',
        },
        notFetchedFilters: [
          {
            id: 'peppol_invoice_inbound',
            designation:
              'hit-components.filters.transaction.peppol-invoice-inbound',
            filter_key: 'peppol_invoice_inbound',
            filter_value: 'is.true',
          },
          {
            id: 'peppol_invoice_outbound',
            designation:
              'hit-components.filters.transaction.peppol-invoice-outbound',
            filter_key: 'peppol_invoice_outbound',
            filter_value: 'is.true',
          },
          {
            id: 'peppol_invoice_inbound_or_outbound',
            designation:
              'hit-components.filters.transaction.peppol-invoice-inbound-or-outbound',
            filter_key: 'or',
            filter_value:
              'peppol_invoice_inbound.is.true,peppol_invoice_outbound.is.true',
          },
        ],
        transform: (data) => {
          if (!data?.peppolInvoiceInbound && !data?.peppolInvoiceOutbound)
            return null;
          return {
            in: data?.peppolInvoiceInbound,
            out: data?.peppolInvoiceOutbound,
          };
        },
        forcedType: 'object',
      }),
      this.TAGS_ATTRIBUTE({
        moduleUsage: 'transaction',
        tableOrigin: 'transaction',
      }),
      new AT.FkArrayStaffAttribute({
        name: 'responsibles',
        onlyFetchDesignation: true,
        validations: {requiredIf: responsiblesRequiredIf},
        joinPath: [
          'transaction_staff!fk_transaction_staff_transaction_id_transaction',
          'staff!fk_transaction_staff_staff_id_staff',
        ],
        tableProperties: {
          header: 'hit-app.common.responsible',
        },
      }),
      new AT.DecimalAttribute({
        name: 'transactionAmount',
        column: 'total_net_amount',
        decimalPrecision: 2,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.net-amount',
          maxWidth: '1fr',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'peppolVatCalculation',
        column: 'peppol_vat_calculation',
      }),
      new AT.DecimalAttribute({
        name: 'transactionAmountGross',
        column: 'total_gross_amount',
        decimalPrecision: 2,
        readOnly: true,
        tableProperties: {
          header: 'hit-app.common.gross-amount',
          maxWidth: '1fr',
        },
      }),
      new AT.ArrayAttribute({
        name: 'documents',
        column: 'document(id, filename, mime_type, creation_date)',
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
      new AT.StringAttribute({
        name: 'reference',
        tableProperties: {
          header: 'hit-app.common.buyer-reference',
        },
      }),
      new AT.StringAttribute({
        name: 'orderReference',
        column: 'order_reference',
        tableProperties: {
          header: 'hit-app.common.order-reference',
        },
      }),
      new AT.StringAttribute({
        name: 'paymentReference',
        column: 'payment_reference',
        tableProperties: {
          header: 'hit-app.common.payment-reference',
        },
      }),
      new AT.StatusHistoryAttribute({
        name: 'logs',
        column: 'transaction_log(status)',
        filterTable: 'transaction_log',
        filterOptions: HISTORY_STATUS_FILTER_OPTIONS,
        tableProperties: {
          header: 'hit-app.transaction.history-logs',
        },
      }),
      new AT.IntegerAttribute({
        name: 'oldDocumentNumber',
        column: 'old_document_no',
        tableProperties: {
          header: 'hit-app.transaction.old-document-number',
          hideByDefault: true,
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'warning',
      }),
      new AT.StringAttribute({
        name: 'memo',
      }),
      new AT.BooleanAttribute({
        name: 'manualEdit',
        column: 'transaction_account_manual_edit',
        default: false,
      }),

      new AT.FkAddressBankAccountAttribute({
        name: 'bankAccount',
        fkToUpdate: 'bank_account_id',
        column: 'bank_account_id',
        joinPath: [
          'address_bank_account!fk_transaction_bank_account_id_address_bank_account',
        ],
        tableProperties: {
          header: 'hit-app.common.bank-account',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'status',
        enumType: 'TRANSACTION_STATUS',
        enumClass: HitOnlineEnums,
        column: 'status',
        tableProperties: {
          header: 'hit-app.transaction.status',
        },
      }),
    ];
  }

  /** True when a log status forbids further edits (see STATUSES_THAT_DISABLE_TRANSACTION_EDITING). */
  get isEditingDisabled() {
    return hasStatusThatDisablesEditing(this.logs);
  }

  /** The transaction can only be reopened if no locking status is present */
  get canBeReopened() {
    return !this.isEditingDisabled && Boolean(this.documentNumber);
  }

  static async fetchLogs(transactionId) {
    if (!transactionId) {
      return [];
    }
    const response = await DataService.read('transaction_log', {
      attributes: 'id,status',
      filters: {transaction_id: `eq.${transactionId}`},
    });
    return response?.data ?? [];
  }

  /** Calculates the period to use in the transaction for the given date */
  static async calculateTransactionPeriod(date = undefined) {
    const dateToUse = date ? date : new Date();
    const dateParam = dateToUse.toISOString().split('T')[0];
    const res = await DataService.read('period', {
      attributes: `id, number:no, designation, beginDate: begin_date, endDate: end_date`,
      filters: {
        begin_date: `lte.${dateParam}`,
        end_date: `gte.${dateParam}`,
        closed: 'is.false',
      },
    });
    if (res.data.length > 0) {
      return new Period(res.data[0]);
    }
    return null;
  }

  static calculateDueDate(documentDate, dueDateConfig) {
    if (!documentDate || !dueDateConfig) return null;

    let dueDate = new Date(documentDate);
    if (dueDateConfig.endOfMonth) {
      dueDate.setMonth(dueDate.getMonth() + 1);
      dueDate.setDate(0);
    }
    dueDate.setDate(dueDate.getDate() + dueDateConfig.days);
    return dueDate;
  }
}
